/**
 * WBS 生成会话态：回退快照上下文 + 生成后是否被手动改过任务。
 * 仅内存态；刷新后回退上下文失效（与 BOM/简介帮写一致）。
 */
import { ref } from 'vue';

export type WbsGenerateRollbackContext = {
  messageId: string;
  projectId: number;
  /** 生成前快照内容；null 表示生成前无快照，回退时清空 */
  previousContent: unknown | null;
  previousExisted: boolean;
  generatedAt: number;
};

const rollbackByMessageId = new Map<string, WbsGenerateRollbackContext>();
/** projectId → 最近一次标记为「手动编辑」的时间戳 */
const manualEditedAtByProject = new Map<number, number>();

/** 手动编辑后递增，供对话按钮禁用态响应式刷新 */
export const wbsManualEditEpoch = ref(0);

export function rememberWbsGenerateRollback(ctx: WbsGenerateRollbackContext) {
  if (!ctx.messageId || !(ctx.projectId > 0)) return;
  // 同一项目仅保留最新一次生成的回退上下文
  for (const [id, old] of [...rollbackByMessageId.entries()]) {
    if (old.projectId === ctx.projectId && id !== ctx.messageId) {
      rollbackByMessageId.delete(id);
    }
  }
  rollbackByMessageId.set(ctx.messageId, ctx);
  // 新一轮生成后清除该项目的手动编辑标记
  manualEditedAtByProject.delete(ctx.projectId);
  wbsManualEditEpoch.value += 1;
}

export function getWbsGenerateRollback(messageId: string): WbsGenerateRollbackContext | null {
  if (!messageId) return null;
  return rollbackByMessageId.get(messageId) ?? null;
}

export function clearWbsGenerateRollback(messageId: string) {
  if (!messageId) return;
  rollbackByMessageId.delete(messageId);
}

export function markProjectWbsManuallyEdited(projectId: number) {
  const id = Number(projectId);
  if (!(id > 0)) return;
  manualEditedAtByProject.set(id, Date.now());
  wbsManualEditEpoch.value += 1;
}

export function clearProjectWbsManualEdit(projectId: number) {
  const id = Number(projectId);
  if (!(id > 0)) return;
  manualEditedAtByProject.delete(id);
}

/** 自 generatedAt 之后是否有手动编辑（内存标记） */
export function isProjectWbsManuallyEditedSince(
  projectId: number,
  generatedAt: number | null | undefined,
): boolean {
  const id = Number(projectId);
  if (!(id > 0)) return false;
  const editedAt = manualEditedAtByProject.get(id);
  if (!editedAt) return false;
  const gen = Number(generatedAt);
  if (!Number.isFinite(gen) || gen <= 0) return true;
  return editedAt > gen;
}
